import apiClient from "../../client/apiClient"
import { appendQueryParams } from "../../utils"

// Breakdown pickers (Report Cards, Marks Tabulation, ...) listed these in
// whatever order the backend's own chronological sort (startDate/createdAt --
// correct for the admin Result Breakdowns management list) happened to
// produce, which has no relation to class level -- "Mid Term (Class X)"
// could land anywhere relative to "Mid Term (KG I)". Sort by the lowest
// Class.order among a breakdown's classes (a breakdown can span several),
// title as the tiebreaker, so pickers read smallest-class-first like the
// Course Structures fix.
export const sortBreakdownsByClass = <T extends { title: string; classes: Array<{ order?: number }> }>(
  items: T[]
): T[] => {
  const minOrder = (b: T) => {
    const orders = b.classes.map(c => c.order).filter((o): o is number => typeof o === "number")
    return orders.length ? Math.min(...orders) : Number.MAX_SAFE_INTEGER
  }
  return [...items].sort((a, b) => minOrder(a) - minOrder(b) || a.title.localeCompare(b.title))
}

export type ResultBreakdown = {
  _id: string
  title: string
  termId: string
  term?: {
    _id: string
    title: string
  }
  sessionId: string
  session?: {
    _id: string
    title: string
  }
  branchId: string
  branch?: {
    _id: string
    name: string
  }
  systemId: string
  system?: {
    _id: string
    name: string
  }
  // Backend always returns this populated (.populate("classes", "name")), so each
  // entry is {_id, name} — never a bare id string, despite what the field name suggests.
  classes: Array<{ _id: string; name: string; order?: number }>
  classesDetails?: Array<{
    _id: string
    name: string
  }>
  startDate: string
  endDate: string
  status: "draft" | "active" | "submitted" | "needs_revision" | "approved" | "published" | "marks_locked" | "inactive"
  createdAt: string
  updatedAt: string
  createdBy?: string
  isDeleted?: boolean
  deletedAt?: string
  deletedBy?: string
}

export type PaginationData = {
  total: number
  totalPages: number
  currentPage: number
  limit: number
  hasNextPage: boolean
  hasPrevPage: boolean
}

export type CreateResultBreakdownData = {
  title: string
  termId: string
  sessionId: string
  branchId: string
  systemId: string
  classes: string[]
  startDate: string
  endDate: string
  status?: "active" | "inactive"
}

export type UpdateResultBreakdownData = Partial<CreateResultBreakdownData>

export type ResultBreakdownsResponse = {
  success: boolean
  data: ResultBreakdown[] 
  pagination: PaginationData
}

const RESULT_BREAKDOWN_ENDPOINT = {
  RESULT_BREAKDOWNS: "/result-breakdown",
}

const getAllResultBreakdowns = async ({
  page = 1,
  limit = 10,
  searchText, // Same as registration (preferred)
  search, // Keep for backward compatibility
  termId,
  session, // Same as registration (preferred)
  sessionId, // Keep for backward compatibility
  branchId,
  systemId,
  classId,
  status,
  all,
}: {
  page?: number
  limit?: number
  searchText?: string // Same as registration (preferred)
  search?: string // Keep for backward compatibility
  termId?: string
  session?: string // Same as registration (preferred)
  sessionId?: string // Keep for backward compatibility
  branchId?: string
  systemId?: string
  classId?: string
  status?: "active" | "inactive"
  // Bypasses the current campus/board auto-scoping -- used by the Edit
  // modal's sibling lookup, which needs to see a matching record on ANY
  // campus, not just whichever one is currently selected.
  all?: boolean
} = {}): Promise<ResultBreakdownsResponse> => {
  const response = await apiClient({
    url: appendQueryParams(RESULT_BREAKDOWN_ENDPOINT.RESULT_BREAKDOWNS, {
      page,
      limit,
      searchText: searchText || search, // Use searchText if provided, else fallback to search
      termId,
      session: session || sessionId, // Use session if provided, else fallback to sessionId
      branchId,
      systemId,
      classId,
      status,
      all: all ? "true" : undefined,
    }),
    method: "GET",
  })
  
  return response.data;
}

const getResultBreakdownById = async (id: string): Promise<ResultBreakdown> => {
  const response = await apiClient({
    url: `${RESULT_BREAKDOWN_ENDPOINT.RESULT_BREAKDOWNS}/${id}`,
    method: "GET",
  })
  return response.data.data;
}

const createResultBreakdown = async (data: CreateResultBreakdownData): Promise<ResultBreakdown> => {
  const response = await apiClient({
    url: RESULT_BREAKDOWN_ENDPOINT.RESULT_BREAKDOWNS,
    method: "POST",
    data,
  })
  return response.data.data;
}

const updateResultBreakdown = async (id: string, data: UpdateResultBreakdownData): Promise<ResultBreakdown> => {
  const response = await apiClient({
    url: `${RESULT_BREAKDOWN_ENDPOINT.RESULT_BREAKDOWNS}/${id}`,
    method: "PUT",
    data,
  })
  return response.data.data; 
}

// Toggle result breakdown status (active/inactive)
const toggleResultBreakdownStatus = async (
  id: string, 
  status: "draft" | "active" | "submitted" | "needs_revision" | "approved" | "published" | "marks_locked" | "inactive"
): Promise<ResultBreakdown> => {
  const response = await apiClient({
    url: `${RESULT_BREAKDOWN_ENDPOINT.RESULT_BREAKDOWNS}/${id}/status`,
    method: "PATCH",
    data: { status },
  })
  return response.data.data;
}

// Delete result breakdown (COMMENTED OUT - Use toggleResultBreakdownStatus instead)
// const deleteResultBreakdown = async (id: string): Promise<{ message: string }> => {
//   const response = await apiClient({
//     url: `${RESULT_BREAKDOWN_ENDPOINT.RESULT_BREAKDOWNS}/${id}`,
//     method: "DELETE",
//   })
//   return response.data;
// }

export {
  getAllResultBreakdowns,
  getResultBreakdownById,
  createResultBreakdown,
  updateResultBreakdown,
  toggleResultBreakdownStatus,
  // deleteResultBreakdown, // COMMENTED OUT - Use toggleResultBreakdownStatus instead
}
